import usePlayersDatabase from "~/composables/supabase/usePlayersDatabase";
import {
  MatchResult,
  MatchResultType,
  OpenToFightCategories,
  Role,
} from "~/types/enums";
import type {
  FighterSearchResult,
  FightersTableFilter,
  PlayerUIModel,
  SortModel,
} from "~/types/players.types";
import { type Database, type Tables } from "~/types/types.gen.js";

export const usePlayerStore = defineStore("playerStore", {
  state: () => ({
    players: [] as PlayerUIModel[],
    userPlayers: [] as PlayerUIModel[],
    countOfPlayersInDatabase: 0,
    playersLoading: false,
    playerEventsLoading: false,
  }),
  getters: {
    userPlayersSortedByAlphabet: (state) => {
      return state.userPlayers.sort((a, b) => a.name.localeCompare(b.name));
    },
    firstFighter: (state) => {
      return state.userPlayers[0];
    },

    getRecordStrings: (state) => (playerData: PlayerUIModel | undefined) => {
      function getRecord(
        category: OpenToFightCategories,
        matchResult: MatchResult,
      ): number {
        return (
          playerData?.player_events?.reduce((acc, event) => {
            if (
              event.fight_type == category &&
              event.end_result == matchResult
            ) {
              return acc + 1;
            }

            return acc;
          }, 0) ?? 0
        );
      }

      if (!playerData) {
        return {
          professional: "0-0-0",
          amateur: "0-0-0",
        };
      }

      const winsExhibition = getRecord(
        OpenToFightCategories.Exhibition,
        MatchResult.WIN,
      );
      const drawsExhibition = getRecord(
        OpenToFightCategories.Exhibition,
        MatchResult.DRAW,
      );
      const lossesExhibition = getRecord(
        OpenToFightCategories.Exhibition,
        MatchResult.LOSS,
      );

      const winsAmateur = getRecord(
        OpenToFightCategories.Amateur,
        MatchResult.WIN,
      );
      const drawsAmateur = getRecord(
        OpenToFightCategories.Amateur,
        MatchResult.DRAW,
      );
      const lossesAmateur = getRecord(
        OpenToFightCategories.Amateur,
        MatchResult.LOSS,
      );

      const winsPro = getRecord(
        OpenToFightCategories.Professional,
        MatchResult.WIN,
      );
      const drawsPro = getRecord(
        OpenToFightCategories.Professional,
        MatchResult.DRAW,
      );
      const lossesPro = getRecord(
        OpenToFightCategories.Professional,
        MatchResult.LOSS,
      );

      return {
        professional: `${winsPro}W - ${drawsPro}D - ${lossesPro}L`,
        amateur: `${winsExhibition + winsAmateur}W - ${
          drawsAmateur + drawsExhibition
        }D - ${lossesAmateur + lossesExhibition}L`,
      };
    },
    getWinStreaks: (state) => (playerData: PlayerUIModel | undefined) => {
      if (!playerData?.player_events?.length) {
        return {
          professional: 0,
          amateur: 0,
        };
      }

      const sortedEvents = [...playerData.player_events].sort(
        (a, b) =>
          new Date(b.event_date || 0).getTime() -
          new Date(a.event_date || 0).getTime(),
      );

      function getCurrentWinStreak(
        categories: OpenToFightCategories[],
      ): number {
        let streak = 0;

        for (const event of sortedEvents) {
          if (!event.fight_type || !categories.includes(event.fight_type)) {
            continue;
          }

          if (event.end_result === MatchResult.WIN) {
            streak++;
            continue;
          }

          break;
        }

        return streak;
      }

      return {
        professional: getCurrentWinStreak([
          OpenToFightCategories.Professional,
        ]),
        amateur: getCurrentWinStreak([
          OpenToFightCategories.Amateur,
          OpenToFightCategories.Exhibition,
        ]),
      };
    },
    getKoTkoCounts: (state) => (playerData: PlayerUIModel | undefined) => {
      function getKoTkoCount(categories: OpenToFightCategories[]): number {
        return (
          playerData?.player_events?.reduce((acc, event) => {
            if (
              event.fight_type &&
              categories.includes(event.fight_type) &&
              event.end_result === MatchResult.WIN &&
              [MatchResultType.KO, MatchResultType.TKO].includes(
                event.end_result_type as MatchResultType,
              )
            ) {
              return acc + 1;
            }

            return acc;
          }, 0) ?? 0
        );
      }

      return {
        professional: getKoTkoCount([OpenToFightCategories.Professional]),
        amateur: getKoTkoCount([
          OpenToFightCategories.Amateur,
          OpenToFightCategories.Exhibition,
        ]),
      };
    },
  },
  actions: {
    async fetchUsersPlayers() {
      const role = useUserStore().user?.role;

      if (
        !useUserStore().isUserLoggedIn ||
        this.userPlayers.length > 0 ||
        ![Role.FIGHTER, Role.GYM_OWNER].includes(role as Role)
      ) {
        return;
      }

      const data = await usePlayersDatabase().fetchUsersPlayers();

      if (data) {
        this.userPlayers = data;
      }
    },

    async addNewPlayer(playerData: PlayerUIModel) {
      const data = await usePlayersDatabase().createPlayer(playerData);

      this.userPlayers.unshift(data);

      useGymStore().onFighterAdded(data);
    },

    async editPlayer(
      playerData: PlayerUIModel,
      options?: { confirmVerificationInvalidation?: boolean },
    ): Promise<PlayerUIModel> {
      if (!playerData.id) {
        throw new Error("editPlayer: Player ID is required");
      }

      const data = await usePlayersDatabase().editPlayer(
        playerData.id,
        playerData,
        options,
      );

      const indexUserPlayers = this.userPlayers.findIndex(
        (player) => player.id == playerData.id,
      );
      if (indexUserPlayers !== -1) {
        this.userPlayers[indexUserPlayers] = data;
      }

      useGymStore().onFighterUpdated(data);

      return data;
    },

    async deletePlayer(id: string) {
      await usePlayersDatabase().deletePlayer(id);

      const indexUserPlayers = this.userPlayers.findIndex(
        (player) => player.id == id,
      );
      if (indexUserPlayers !== -1) {
        this.userPlayers.splice(indexUserPlayers, 1);
      }

      useGymStore().onFighterDeleted(id);
    },

    async removePlayerFromGym(id: string) {
      await usePlayersDatabase().removePlayerFromGym(id);

      const indexUserPlayers = this.userPlayers.findIndex(
        (player) => player.id == id,
      );
      if (indexUserPlayers !== -1) {
        this.userPlayers.splice(indexUserPlayers, 1);
      }

      useGymStore().onFighterDeleted(id);
    },

    async getPlayerData(
      id: string | undefined,
    ): Promise<PlayerUIModel | undefined> {
      if (id == undefined || id.length == 0) {
        return undefined;
      }

      // Check cache first
      // var playerData: PlayerUIModel | undefined = [
      //   ...this.players,
      //   ...this.userPlayers,
      // ].find((player) => player.id === id);

      // if (
      //   playerData &&
      //   playerData.age !== undefined &&
      //   playerData.matched_categories !== undefined
      // ) {
      //   return playerData;
      // }

      // Fetch from server API
      const data = await usePlayersDatabase().getPlayerData(id);

      if (data) {
        // Find existing player in cache
        // const existingPlayerIndex = this.players.findIndex(
        //   (player) => player.id === data.id,
        // );

        // // Update or add to players array
        // if (existingPlayerIndex !== -1) {
        //   this.players[existingPlayerIndex] = data;
        // } else {
        //   this.players.push(data);
        // }

        // Commented for now to fix a bug where fetchPlayersForUser was not working
        // because getPlayerData was called before fetchPlayersForUser was called

        // Update or add to userPlayers array if user is member of the gym
        // if (useGymStore().isUserMemberOfGym(data.gym_id)) {
        //   const existingUserPlayerIndex = this.userPlayers.findIndex(
        //     (player) => player.id === data.id
        //   );

        //   if (existingUserPlayerIndex !== -1) {
        //     this.userPlayers[existingUserPlayerIndex] = data;
        //   } else {
        //     this.userPlayers.push(data);
        //   }
        // }

        return data;
      }

      return undefined;
    },

    async getFightersForQuery(
      q: string | undefined,
      currentFighterId: string | undefined,
    ): Promise<FighterSearchResult[]> {
      if (q == undefined || q.length == 0) {
        return [];
      }

      const queryTrimmed = q.trim();

      try {
        return await $fetch<FighterSearchResult[]>("/api/fighters/search", {
          query: {
            q: queryTrimmed,
            exclude_id: currentFighterId,
          },
        });
      } catch (error) {
        usePosthog().logError(
          error,
          "players.ts:getFightersForQuery()",
          "searching fighters failed",
        );
      }

      return [];
    },

    /**
     * READ: Fetches all events for a specific player and sorts them by date.
     * @param {string} playerId - The UUID of the player.
     */
    // async fetchPlayerEvents(playerId: string): Promise<Tables<"player_events">[]> {
    //   const supabase = useSupabaseClient();
    //   this.loading = true;
    //   this.error = null;

    //   try {
    //     const { data, error } = await supabase
    //       .from('player_events')
    //       .select('*')
    //       .eq('player_id', playerId)
    //       .order('event_date', { ascending: false });

    //     if (error) throw error;
    //     return data as Tables<"player_events">[];
    //   } catch (err: any) {
    //     this.error = err.message;
    //     console.error('Error fetching player events:', err);
    //   } finally {
    //     this.loading = false;
    //   }
    // },

    /**
     * CREATE: Adds a new event for a player.
     * @param {PlayerEventInsert} newEventData - The data for the new event.
     */
    async addPlayerEvent(newEventData: Partial<Tables<"player_events">>) {
      const supabase = useSupabaseClient();
      this.playerEventsLoading = true;

      try {
        const { data: newEvent, error } = await supabase
          .from("player_events")
          .insert(newEventData as any)
          .select()
          .single();

        if (error) throw error;

        if (newEvent) {
          const addEventToPlayer = (playerArray: any[]) => {
            // 1. Find the player object directly in the state array.
            const playerToUpdate = playerArray.find(
              (p) => p.id === newEvent.player_id,
            );

            if (!playerToUpdate) return;

            // 2. Ensure the player_events array exists.
            if (!playerToUpdate.player_events) {
              playerToUpdate.player_events = [];
            }

            // 3. Mutate the array by adding the new event. unshift() adds to the beginning.
            playerToUpdate.player_events.unshift(newEvent);

            // 4. Sort the existing array in place. .sort() mutates the array directly.
            playerToUpdate.player_events.sort((a: any, b: any) => {
              return (
                new Date(b.event_date).getTime() -
                new Date(a.event_date).getTime()
              );
            });

            useGymStore().onFighterUpdated(playerToUpdate);
          };

          addEventToPlayer(this.players);
          addEventToPlayer(this.userPlayers);
        }
      } catch (err: any) {
        displayErrorNotification();

        usePosthog().posthogLogError({
          err,
          context: {
            userId: useUserStore().user?.id,
            action: "addPlayerEvent",
          },
        });

        throw err;
      } finally {
        this.playerEventsLoading = false;
      }
    },

    /**
     * UPDATE: Updates an existing player event.
     * @param {number} eventId - The ID of the event to update.
     * @param {PlayerEventUpdate} updates - An object with the fields to update.
     */
    async updatePlayerEvent(updates: Partial<Tables<"player_events">>) {
      const supabase = useSupabaseClient();
      this.playerEventsLoading = true;

      try {
        const { data: updatedEvent, error } = await supabase
          .from("player_events")
          .update(updates as never)
          .eq("id", updates.id!)
          .select()
          .single();

        if (error) throw error;

        if (updatedEvent) {
          // This function finds and mutates the player and their events in place.
          const updatePlayerInArray = (playerArray: any[]) => {
            // 1. Find the specific player object in the array without creating a new array.
            const playerToUpdate = playerArray.find(
              (p) => p.id === updatedEvent.player_id,
            );

            // If the player isn't in this list, do nothing.
            if (!playerToUpdate) return;

            // Ensure the player_events array exists.
            if (!playerToUpdate.player_events) {
              playerToUpdate.player_events = [];
            }

            // 2. Find the index of the event to be updated.
            const eventIndex = playerToUpdate.player_events.findIndex(
              (e: any) => e.id === updatedEvent.id,
            );

            // 3. Mutate the array in place.
            if (eventIndex !== -1) {
              // If the event exists, replace it at its current index.
              playerToUpdate.player_events[eventIndex] = updatedEvent;
            } else {
              // If the event is new to this list (e.g., after pagination), add it.
              playerToUpdate.player_events.push(updatedEvent);
            }

            // 4. Sort the existing array in place. .sort() mutates the array.
            playerToUpdate.player_events.sort((a: any, b: any) => {
              return (
                new Date(b.event_date).getTime() -
                new Date(a.event_date).getTime()
              );
            });

            useGymStore().onFighterUpdated(playerToUpdate);
          };

          // Apply the mutation logic to both state arrays
          updatePlayerInArray(this.players);
          updatePlayerInArray(this.userPlayers);
        }
      } catch (err: any) {
        displayErrorNotification();

        usePosthog().posthogLogError({
          err,
          context: {
            userId: useUserStore().user?.id,
            action: "updatePlayerEvent",
          },
        });
        throw err;
      } finally {
        this.playerEventsLoading = false;
      }
    },

    /**
     * DELETE: Removes a player event from the database.
     * @param {number} eventId - The ID of the event to delete.
     */
    async deletePlayerEvent(fighterId: string, eventId: string) {
      const supabase = useSupabaseClient<Database>();
      this.playerEventsLoading = true;

      try {
        const { error } = await supabase
          .from("player_events")
          .delete()
          .eq("id", eventId)
          .eq("player_id", fighterId);

        if (error) throw error;

        const deleteEventFromPlayer = (playerArray: any[]) => {
          // 1. Find the player object directly in the state array.
          const playerToUpdate = playerArray.find((p) => p.id === fighterId);

          // 2. If player or their events don't exist, do nothing.
          if (!playerToUpdate || !playerToUpdate.player_events) {
            return;
          }

          // 3. Find the index of the event to remove.
          const eventIndex = playerToUpdate.player_events.findIndex(
            (event: any) => event.id === eventId,
          );

          // 4. If found, mutate the array in place using splice().
          if (eventIndex !== -1) {
            playerToUpdate.player_events.splice(eventIndex, 1);
          }

          useGymStore().onFighterUpdated(playerToUpdate);
        };

        deleteEventFromPlayer(this.players);
        deleteEventFromPlayer(this.userPlayers);
      } catch (err: any) {
        displayErrorNotification();

        usePosthog().posthogLogError({
          err,
          context: {
            userId: useUserStore().user?.id,
            action: "deletePlayerEvent",
          },
        });

        throw err;
      } finally {
        this.playerEventsLoading = false;
      }
    },

    /**
     * Invalidates (removes) a player from the cache by their ID.
     * The next fetch will get fresh data from the database.
     * @param {string} playerId - The UUID of the player to invalidate.
     */
    invalidatePlayerCacheById(playerId: string | null) {
      if (!playerId) return;

      const playerIndex = this.players.findIndex((p) => p.id === playerId);
      if (playerIndex !== -1) {
        this.players.splice(playerIndex, 1);
      }

      const userPlayerIndex = this.userPlayers.findIndex(
        (p) => p.id === playerId,
      );
      if (userPlayerIndex !== -1) {
        this.userPlayers.splice(userPlayerIndex, 1);
      }
    },
  },
});
